
'use client'
    
import { collection, doc, getDoc, getDocs, query, where, documentId } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { ArtworkSet, ArtItem, Artist } from '@/types';
import { Header } from '@/components/header';
import Link from 'next/link';
import { notFound, useParams } from 'next/navigation';
import { ArtCard } from '@/components/art-card';
import { useEffect, useState } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { Footer } from '@/components/footer';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';

async function getArtworkSet(id: string): Promise<ArtworkSet | null> {
    const docRef = doc(db, 'artwork_sets', id);
    try {
        const docSnap = await getDoc(docRef);
        
        if (!docSnap.exists() || docSnap.data().status !== 'public') {
            return null;
        }

        const data = docSnap.data();
        return {
            id: docSnap.id,
            ...data,
        } as ArtworkSet;
    } catch (error) {
        console.error("Error fetching artwork set:", error);
        return null; // Return null on permission error or other issues
    }
}

async function getArtworksByIds(ids: string[]): Promise<ArtItem[]> {
    if (!ids || ids.length === 0) return [];
    
    const artworksCol = collection(db, 'artworks');
    const artworksList: ArtItem[] = [];

    // Firestore 'in' query is limited to 30 items, so process in batches.
    const idBatches: string[][] = [];
    for (let i = 0; i < ids.length; i += 30) {
        idBatches.push(ids.slice(i, i + 30));
    }

    try {
        for (const batch of idBatches) {
            const q = query(artworksCol, where(documentId(), 'in', batch));
            const snapshot = await getDocs(q);
            snapshot.docs.forEach(doc => {
                const data = doc.data();
                artworksList.push({
                    id: doc.id,
                    imageUrls: data.imageUrls || [data.imageUrl || 'https://placehold.co/600x800.png'],
                    ...data
                } as ArtItem);
            });
        }
    } catch (error) {
        console.error("Error fetching artworks by ID:", error);
        return []; // Return empty list on error
    }

    return artworksList;
}

export default function SetDetailPage() {
    const params = useParams();
    const id = typeof params.id === 'string' ? params.id : '';
    const [set, setSet] = useState<ArtworkSet | null>(null);
    const [artworks, setArtworks] = useState<ArtItem[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!id) return;

        const fetchData = async () => {
            setLoading(true);
            try {
                const setData = await getArtworkSet(id);
                if (setData) {
                    setSet(setData);
                    const artworksData = await getArtworksByIds(setData.artworkIds);
                    // Preserve order from artworkIds
                    const orderedArtworks = setData.artworkIds.map(id => artworksData.find(art => art.id === id)).filter(Boolean) as ArtItem[];
                    setArtworks(orderedArtworks);
                } else {
                    setSet(null); // Explicitly set to null if not found
                }
            } catch (error) {
                console.error("Failed to fetch set data:", error);
                setSet(null);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [id]);

    if (loading) {
        return (
            <div className="flex flex-col min-h-screen">
                <Header />
                <main className="flex-1 container mx-auto px-4 py-8">
                    <Skeleton className="h-10 w-1/4 mb-2" />
                    <Skeleton className="h-6 w-1/2 mb-8" />
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
                        {Array.from({ length: 4 }).map((_, i) => (
                           <Skeleton key={i} className="h-[350px] w-full rounded-lg" />
                        ))}
                    </div>
                </main>
                <Footer />
            </div>
        );
    }

    if (!set) {
        notFound();
    }

    return (
        <div className="flex flex-col min-h-screen">
            <Header />
            <main className="flex-1">
                <div className="container mx-auto px-4 py-8">
                     <div className="mb-8">
                        <Button asChild variant="ghost" className="mb-4">
                             <Link href="/sets">
                                <ArrowLeft className="mr-2"/>
                                Back to All Sets
                            </Link>
                        </Button>
                        <h1 className="font-headline text-4xl mb-2">{set.name}</h1>
                        {set.description && (
                             <p className="text-lg text-muted-foreground max-w-3xl">{set.description}</p>
                        )}
                    </div>
                   
                    {artworks.length > 0 ? (
                         <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
                            {artworks.map((item, index) => (
                                <ArtCard key={item.id} item={item} priority={index < 4}/>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-16 border rounded-lg">
                            <h3 className="text-xl font-semibold">No Artwork Found in this Set</h3>
                            <p className="text-muted-foreground mt-2">This set currently contains no artworks.</p>
                        </div>
                    )}
                </div>
            </main>
            <Footer />
        </div>
    );
}
